import { Component } from '@angular/core';
import { ThemeService } from './services/theme.service';
import { TranslateService } from '@ngx-translate/core';
import { parse } from 'qs';
import { Router, NavigationEnd, ActivatedRoute, NavigationStart } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import store from 'store';

import * as SettingsActions from 'src/app/store/settings/actions';
import { EventManagerService } from './services/event-manager.service';
import { UserSessionService } from './services/user-session.service';
import { FormOperationService } from './services/form-manager.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { defaultConfig } from './app.general';

import english from './locales/en-US';
import french from './locales/fr-FR';
import russian from './locales/ru-RU';
import chinese from './locales/zh-CN';

const { warningDuration: WARNING_DURATION } = defaultConfig;

const locales = {
  'en-US': english,
  'fr-FR': french,
  'ru-RU': russian,
  'zh-CN': chinese,
};

@Component({
  selector: 'app-root',
  template: `
    <nz-layout>
      <nz-content>
        <app-activity-check></app-activity-check>
        <router-outlet></router-outlet>
      </nz-content>
    </nz-layout>`,
  styleUrls: ['./app.component.less'],
})
export class AppComponent {
  _locale: String;
  _theme: String;
  sessionExpirationTimer: any = null;
  autoCloseTimeout: any = null;
  isCollapsed = false;
  inClosing = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private _store: Store<any>,
    private _eventManager: EventManagerService,
    private _userSessionService: UserSessionService,
    private _formOperationService: FormOperationService,
    private modal: NzModalService,
    translate: TranslateService,
    private themeService: ThemeService
  ) { }

  async ngOnInit() {
    // set page title from router data variable
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data),
      )
      .subscribe(event => this.titleService.setTitle(defaultConfig.applicationName + ' | ' + event['title']));

    // listen url query params and set them to ngrx store
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const queryString = event.url.match(/\?(.*)/);
        if (queryString) {
          const queryParams = parse(queryString[1]);
          const keys = Object.keys(queryParams);
          if (keys.length) {
            keys.forEach((key) => {
              let value;
              switch (queryParams[key]) {
              case 'false':
                value = false;
                break;
              case 'true':
                value = true;
                break;
              default:
                value = queryParams[key];
                break;
              }
              this._store.dispatch(
                new SettingsActions.SetStateAction({ [key]: value }),
              );
            });
          }
        }
      });

    // detecting & set mobile/tablet/desktop viewports
    const setViewPort = (isMobileView: any = false, isTabletView: any = false) => {
      this._store.dispatch(
        new SettingsActions.SetStateAction({ isMobileView }),
      );
      this._store.dispatch(
        new SettingsActions.SetStateAction({ isTabletView }),
      );
    };
    const detectViewPort = (load = false) => {
      const _isMobileView = window.innerWidth < 768;
      const _isTabletView = window.innerWidth < 992;
      const _isDesktopView = !_isMobileView && !_isTabletView;
      const isMobileView = JSON.parse(window.localStorage.getItem('app.settings.isMobileView'));
      const isTabletView = JSON.parse(window.localStorage.getItem('app.settings.isTabletView'));
      const isDesktopView = !isMobileView && !isTabletView;
      if (_isDesktopView && (_isDesktopView !== isDesktopView || load)) {
        setViewPort(false, false);
      }
      if (_isTabletView && !_isMobileView && (_isTabletView !== isTabletView || load)) {
        setViewPort(false, true);
        this._store.dispatch(
          new SettingsActions.SetStateAction({ isMenuCollapsed: true }),
        );
      }
      if (_isMobileView && (_isMobileView !== isMobileView || load)) {
        setViewPort(true, false);
      }
    };
    detectViewPort(true);
    window.addEventListener('resize', () => {
      detectViewPort();
    });

    // set primary color on app load
    const primaryColor = () => {
      const color = store.get('app.settings.primaryColor');
      if (color) {
        const addStyles = () => {
          const styleElement = document.querySelector('#primaryColor');
          if (styleElement) {
            styleElement.remove();
          }
          const body = document.querySelector('body');
          const styleEl = document.createElement('style');
          const css = document.createTextNode(`:root { --kit-color-primary: ${color};}`);
          styleEl.setAttribute('id', 'primaryColor');
          styleEl.appendChild(css);
          body.appendChild(styleEl);
        };
        addStyles();
        this._store.dispatch(
          new SettingsActions.SetStateAction({ primaryColor: color }),
        );
      }
    };
    primaryColor();
    await this.startApp();
  }

  setTheme = (theme) => {
    document.querySelector('html').setAttribute('data-kit-theme', theme);
    if (theme === 'default') {
      this._store.dispatch(
        new SettingsActions.SetStateAction({ menuColor: 'gray' }), // 'light'
      );
    }
    if (theme === 'dark') {
      this._store.dispatch(
        new SettingsActions.SetStateAction({ menuColor: 'dark' }),
      );
    }
  };

  toggleTheme(): void {
    this.themeService.toggleTheme().then();
  }

  async startApp() {
    await this._formOperationService.loadStack();
    this._userSessionService.activeSession();
    this._eventManager.subscribe('sessionToBeClosed', () => this.showSessionCloseWarning());
  }

  showSessionCloseWarning() {
    if (this.inClosing) {
      return;
    }
    this.inClosing = true;
    const modal = this.modal.warning({
      nzTitle: 'Cierre de sesión por inactividad',
      nzContent: 'Su sesión está a punto de expirar, presione OK para mantener abierta la sesión',
      nzOnOk: this.keepSessionOpen.bind(this),
    });
    this.autoCloseTimeout = setTimeout(() => {
      this.inClosing = false;
      modal.destroy();
      this._eventManager.next('sessionEnded', null);
    }, WARNING_DURATION);
  }

  keepSessionOpen() {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout);
      this.autoCloseTimeout = null;
    }
    this._userSessionService.activeSession();
  }
}
